import { Dispatch, SetStateAction, useCallback } from "react";
import {
  BuildOutlined,
  CategoryOutlined,
  Download,
  FormatQuoteOutlined,
  Save as SaveIcon,
  TableChartOutlined,
} from "@mui/icons-material";
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import SourcesTab from "src/reusable/Sidebar/tabs/SourcesTab/SourcesTab";
import ArtifactsTab from "src/reusable/Sidebar/tabs/ArtifactsTab/ArtifactsTab";
import SourcesDownloadButton from "src/views/assistant/components/sources/SourcesDownloadButton";
import SourcesViewToggle from "src/views/assistant/components/sources/SourcesViewToggle";
import AutoOpenToggle from "src/reusable/Sidebar/AutoOpenToggle";
import PartsAIFeatureSheet from "../PartsAIFeatureSheet";
import LinkedPartsTab from "../components/sidebar/LinkedPartsTab";
import { SidebarConfig } from "src/reusable/Sidebar/types";
import { NucToast } from "src/reusable/components/Toast";

export interface PartsAISidebarContext {
  responseSources: any[];
  artifacts: Record<string, any>;
  viewMode: "dataChunk" | "cardList";
  setViewMode: Dispatch<SetStateAction<"dataChunk" | "cardList">>;
  partsAIRunId: number | undefined;
  featureSheetRowCount: number;
  featureSheetFeatures: Record<string, any> | null;
  // Save-action wiring — flow through context so the Feature Sheet tab's
  // `renderHeaderActions` can render the Save button inside the sidebar
  // header (correct positioning, only shown while the sidebar is open).
  // `isRunDataLoaded` gates both buttons until the run-detail query has
  // resolved: until then we can't tell whether this is an existing-part
  // chat (Save changes to part) or a fresh extraction (Save as Part), so
  // we hide the button entirely to avoid briefly offering the wrong action.
  isRunDataLoaded: boolean;
  isEditingExistingPart: boolean;
  isSavingChanges: boolean;
  onSaveChangesToPart: () => void;
  onOpenSaveAsPartModal: () => void;
  // Parts tab — list of parts pinned to this dialogue (search hits, the two
  // comparison parts, or the part being edited).
  dialogueId: number | undefined;
  linkedPartCount: number;
}

/** Download feature sheet features as CSV */
const downloadFeatureSheetCSV = (features: Record<string, any>) => {
  const entries = Object.entries(features);
  if (entries.length === 0) {
    NucToast({ message: "No data to export.", type: "warning" });
    return;
  }
  const escape = (v: any) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const csv = [
    "Feature,Value",
    ...entries.map(([k, v]) => `${escape(k)},${escape(v)}`),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "feature_sheet.csv";
  a.click();
  URL.revokeObjectURL(url);
};

export const usePartsAISidebar = (
  context: PartsAISidebarContext,
): SidebarConfig<PartsAISidebarContext> => {
  const { preferences } = useUIStore();

  const handleDownloadCSV = useCallback(() => {
    if (context.featureSheetFeatures) {
      downloadFeatureSheetCSV(context.featureSheetFeatures);
    }
  }, [context.featureSheetFeatures]);

  return {
    tabs: [
      {
        id: "sources",
        label: "Sources",
        icon: FormatQuoteOutlined,
        renderContent: ({ context }) => {
          if (!context) return null;
          return (
            <SourcesTab
              sources={context.responseSources}
              viewMode={context.viewMode}
              setViewMode={context.setViewMode}
              detailSourceViewSdrId={null}
              detailSourceViewSourceId={null}
              setDetailSourceViewSdrId={() => {}}
              setDetailSourceViewSourceId={() => {}}
              // Three-tier provenance funnel (#20423 Story 4): without the
              // dialogue id SourcesTab silently falls back to the legacy list.
              dialogueId={context.dialogueId ?? null}
            />
          );
        },
        renderHeaderActions: ({ context }) => {
          if (!context) return null;
          return (
            <>
              <SourcesDownloadButton
                responseSources={context.responseSources}
              />
              <SourcesViewToggle
                alignment={context.viewMode}
                handleAlignment={(_, newValue) => {
                  if (newValue) {
                    context.setViewMode(newValue as "dataChunk" | "cardList");
                  }
                }}
              />
              <AutoOpenToggle />
            </>
          );
        },
        getBadgeContent: (context) => {
          return context?.responseSources.length ?? 0;
        },
      },
      {
        id: "artifacts",
        label: "Artifacts",
        icon: CategoryOutlined,
        renderContent: ({ context }) => {
          if (!context) return null;
          return <ArtifactsTab artifacts={context.artifacts} />;
        },
        renderHeaderActions: () => <AutoOpenToggle />,
        getBadgeContent: (context) => {
          return context?.artifacts ? Object.keys(context.artifacts).length : 0;
        },
      },
      {
        id: "parts" as const,
        label: "Parts",
        icon: BuildOutlined,
        renderContent: ({ context }: { context?: PartsAISidebarContext }) => {
          if (!context) return null;
          return <LinkedPartsTab dialogueId={context.dialogueId} />;
        },
        renderHeaderActions: () => <AutoOpenToggle />,
        getBadgeContent: (context?: PartsAISidebarContext) =>
          context?.linkedPartCount ?? 0,
      },
      ...(context.partsAIRunId
        ? [
            {
              id: "featureSheet" as const,
              label: "Feature Sheet",
              icon: TableChartOutlined,
              renderContent: ({
                context,
              }: {
                context?: PartsAISidebarContext;
              }) => {
                if (!context?.partsAIRunId) return null;
                return (
                  <PartsAIFeatureSheet partsAIRunId={context.partsAIRunId} />
                );
              },
              renderHeaderActions: ({
                context,
              }: {
                context?: PartsAISidebarContext;
              }) => {
                // Hide both Save buttons until the run-detail query has
                // resolved — otherwise we briefly render the wrong one
                // (default-false `isEditingExistingPart`) for existing-part
                // chats while the metadata is still loading.
                const showSave =
                  !!context &&
                  context.partsAIRunId !== undefined &&
                  context.featureSheetRowCount > 0 &&
                  context.isRunDataLoaded;
                return (
                  <>
                    {showSave && context.isEditingExistingPart && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          context.isSavingChanges ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <SaveIcon fontSize="small" />
                          )
                        }
                        onClick={context.onSaveChangesToPart}
                        disabled={context.isSavingChanges}
                      >
                        {context.isSavingChanges
                          ? "Saving..."
                          : "Save changes to part"}
                      </Button>
                    )}
                    {showSave && !context.isEditingExistingPart && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon fontSize="small" />}
                        onClick={context.onOpenSaveAsPartModal}
                      >
                        Save as Part
                      </Button>
                    )}
                    <Tooltip title="Download CSV">
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleDownloadCSV}
                          disabled={
                            !context?.featureSheetFeatures ||
                            context.featureSheetRowCount === 0
                          }
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <AutoOpenToggle />
                  </>
                );
              },
              getBadgeContent: (context?: PartsAISidebarContext) =>
                context?.featureSheetRowCount ?? 0,
            },
          ]
        : []),
    ],
    defaultTab: "sources",
    responsive: {
      enableMobile: true,
      enableDesktopNav: true,
      enableResize: true,
      minWidth: 300,
      maxWidth: 800,
      defaultWidth: 400,
    },
    autoOpen: {
      enabled: () => preferences?.autoOpenSidebar ?? true,
      getContentCounts: (context) => ({
        sources: context?.responseSources.length ?? 0,
        artifacts: context?.artifacts
          ? Object.keys(context.artifacts).length
          : 0,
        // Tracking the linked-parts count keeps the sidebar consistent with
        // the Parts tab badge.
        parts: context?.linkedPartCount ?? 0,
      }),
      autoSelectTab: "sources",
      triggerKey: "sources",
    },
  };
};
